import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logoMenu from "../../static/media/logo_menu.webp";

const SECTIONS = [
  { id: "inicio", label: "Inicio" },
  { id: "equipo", label: "Nosotros" },
  { id: "servicios", label: "Servicios" },
  { id: "ubicacion", label: "Ubícanos" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("inicio");
  const [scrolled, setScrolled] = useState(false);

  const bg = useMemo(() => (scrolled ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.35)"), [scrolled]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "0px 0px -35% 0px", threshold: [0.25, 0.5, 0.75] }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 84;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
    setOpen(false);
  };

  const openWhatsApp = () => {
    window.open("https://wa.me/593987763815?text=Hola%2C%20necesito%20informaci%C3%B3n", "_blank");
  };

  const navItemSx = (isActive) => ({
    px: 2.2,
    py: 1,
    borderRadius: "999px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: ".95rem",
    color: "#fff",
    border: isActive ? "1px solid rgba(128,0,255,0.7)" : "1px solid transparent",
    background: isActive ? "linear-gradient(90deg, rgba(58,0,116,0.25) 0%, rgba(128,0,255,0.20) 100%)" : "transparent",
    transition: "all .25s ease",
    "&:hover": {
      borderColor: "rgba(128,0,255,0.6)",
      background: "linear-gradient(90deg, rgba(58,0,116,0.18) 0%, rgba(128,0,255,0.15) 100%)",
    },
  });

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1200,
        background: bg,
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(128,0,255,0.15)",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          onClick={() => scrollToSection("inicio")}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={logoMenu}
            alt="Dobledx"
            sx={{
                width: { xs: 120, sm: 135 },
                height: "auto",
                display: "block",
                objectFit: "contain",
                filter:
                "drop-shadow(0 0 4px rgba(255,255,255,0.9)) drop-shadow(0 0 8px rgba(128,0,255,0.4))",
            }}
          />
        </Box>

        <Stack direction="row" alignItems="center" spacing={3} sx={{ display: { xs: "none", md: "flex" } }}>
          {SECTIONS.map((s) => (
            <Box key={s.id} sx={navItemSx(active === s.id)} onClick={() => scrollToSection(s.id)}>
              {s.label}
            </Box>
          ))}
          <Button
            onClick={openWhatsApp}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              color: "#fff",
              px: 3,
              py: 1.1,
              borderRadius: "999px",
              background: "linear-gradient(90deg, #3A0074 0%, #8000FF 100%)",
              boxShadow: "0 0 18px rgba(128,0,255,0.35)",
              "&:hover": { background: "linear-gradient(90deg, #8000FF 0%, #3A0074 100%)" },
            }}
          >
            Agendar Cita
          </Button>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ display: { xs: "flex", md: "none" } }}>
          <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".95rem" }}>MENÚ</Typography>
          <IconButton onClick={() => setOpen(true)} sx={{ color: "#fff" }}>
            <MenuIcon />
          </IconButton>
        </Stack>
      </Box>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: "86vw",
            maxWidth: 340,
            height: "100%",
            background: "linear-gradient(180deg, #12001F 0%, #000 100%)",
            color: "#fff",
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ mt: 1 }}>
            {SECTIONS.map((s) => (
              <ListItem key={s.id} disablePadding sx={{ mb: 1.2 }}>
                <Box
                  onClick={() => scrollToSection(s.id)}
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    py: 1.1,
                    borderRadius: "12px",
                    border: active === s.id ? "1px solid rgba(128,0,255,0.7)" : "1px solid rgba(255,255,255,0.08)",
                    background:
                      active === s.id
                        ? "linear-gradient(90deg, rgba(58,0,116,0.25) 0%, rgba(128,0,255,0.20) 100%)"
                        : "rgba(255,255,255,0.02)",
                    fontWeight: 800,
                  }}
                >
                  {s.label}
                </Box>
              </ListItem>
            ))}
            <ListItem disablePadding sx={{ mt: 1.5 }}>
              <Button
                fullWidth
                onClick={openWhatsApp}
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
                  py: 1.2,
                  borderRadius: "14px",
                  background: "linear-gradient(90deg, #3A0074 0%, #8000FF 100%)",
                  boxShadow: "0 0 18px rgba(128,0,255,0.35)",
                  "&:hover": { background: "linear-gradient(90deg, #8000FF 0%, #3A0074 100%)" },
                }}
              >
                Agendar Cita
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
